import { NgRedux, select } from '@ptl/angular-redux-store';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, FormControl, UntypedFormGroup } from '@angular/forms';
import { StateStructure } from '@ptl/state';
import { Observable } from 'rxjs';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { DRXRetrieveSingleProductResponse, PharmacyCost } from 'src/app/shared/interfaces/drx.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { RawPharmacy } from '../configs/interfaces/pharmacy-lookup/response/response.interface';
import { Pharmacy } from '../configs/interfaces/pharmacy.interface';
import { PharmacyState, PHARMACY_REDUX_STATE_ID, RETAIL_PHARMACY_TYPE_ID, selectPharmacy } from '../configs/reducers/pharmacy.reducer';
import { PharmacyInformationService } from '../services/pharmacy-information.service';

@Component({
    selector: 'app-pharmacy-detail',
    templateUrl: './pharmacy-detail.component.html',
    styleUrls: ['./pharmacy-detail.component.scss'],
    standalone: false
})
export class PharmacyDetailComponent implements OnInit {

  PHARM_CONTROL = 'pharmControl';

  allPharmacys: Pharmacy[] = [];
  selectedPharmacy: Pharmacy;

  pharmForm: UntypedFormGroup;

  @select(['componentStates', 'DrxServiceData', 'drxSingleProductResponse'])
  planRefreshWatcher$: Observable<DRXRetrieveSingleProductResponse>;

  @select(['componentStates', 'DrxServiceData', 'drxSessionId'])
  sessionWatcher$: Observable<string>;

  constructor(
    private fb: UntypedFormBuilder,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>
  ) {
    this.updatePharmacyNetworks = this.updatePharmacyNetworks.bind(this);
    this.changePharmacy = this.changePharmacy.bind(this);
    this.updatePharmacyInfo = this.updatePharmacyInfo.bind(this);

    this.pharmForm = this.fb.group({
      pharmControl: this.fb.control('')
    });
  }

  async ngOnInit() {
    const pharmacyState: PharmacyState = this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID);

    // This component doesn't do anything without pharmacies
    if (!pharmacyState.pharmacys?.length) {
      return;
    }

    // set the component's pharmacys objects
    this.allPharmacys = pharmacyState.pharmacys;
    this.selectedPharmacy = pharmacyState.selectedPharmacy;
    this.selectPharmacy(this.selectedPharmacy);

    // Listen for form changes to dispatch events
    this.pharmForm.valueChanges.subscribe(this.changePharmacy);

    // Listen for plan changes to refresh our pharmacy network status
    this.planRefreshWatcher$.subscribe(this.updatePharmacyInfo);
  }

  changePharmacy(formValue: {pharmControl: string}) {
    // No change events if there are no pharmacies selected or we selected the same pharmacy
    if (!this.allPharmacys?.length || this.selectedPharmacy.pharmacyId === formValue.pharmControl) {
      return;
    }

    let newSelectedPharmacy = this.allPharmacys.filter((pharm) => pharm.pharmacyId === formValue.pharmControl)[0];
    this.selectedPharmacy = newSelectedPharmacy;
    this.ngRedux.dispatch(selectPharmacy(newSelectedPharmacy));
  }

  selectPharmacy(pharmacy: Pharmacy) {
    this.pharmForm.controls[this.PHARM_CONTROL].setValue(pharmacy.pharmacyId);
  }

  updatePharmacyInfo(newState: DRXRetrieveSingleProductResponse) {
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const currentPlanId = planState.medicareAdvantage_or_PDP_planForDRX.attributes.PBP_ID;

    // Check if pharmacy costs are available and we have the correct plan's data
    if (!newState || !newState.PharmacyCosts || newState.PlanID !== currentPlanId) {
      return;
    }

    this.updatePharmacyNetworks(newState);
  }

  updatePharmacyNetworks(newState: DRXRetrieveSingleProductResponse): void {
    // The UI will ignore a 'null' value. Must be explicitly true or false to display
    let planPharmacyCosts = newState.PharmacyCosts as PharmacyCost[];

    this.allPharmacys.forEach((pharmacy) => {
      pharmacy.inNetwork = null;

      for (let i = 0; i < planPharmacyCosts.length; i++) {
        if (pharmacy.pharmacyId === planPharmacyCosts[i].PharmacyID) {
          pharmacy.inNetwork = planPharmacyCosts[i].IsNetwork;
          pharmacy.isPreferred = planPharmacyCosts[i].IsPreferred;
          break;
        }
      }
    });
  }
}
